import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, ArrowsClockwise, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { fmtDateTime } from "@/components/communication/commMeta";
import ScanInboundLog from "@/components/communication/ScanInboundLog";

const STEPS = (domain) => [
  `Resend → Domains → „Add Domain“ → ${domain} eintragen und die Region wählen.`,
  `Bei der Domain in Resend „Receiving“ aktivieren. Resend zeigt einen MX-Eintrag an (Name: ${domain.split(".")[0]}, Wert: inbound-smtp…, Priorität 10).`,
  `Beim Domain-Anbieter von ${domain.split(".").slice(1).join(".")} (DNS-Verwaltung) genau diesen MX-Eintrag anlegen. Bestehende MX-Einträge der Hauptdomain NICHT ändern.`,
  "In Resend „Verify“ klicken (DNS-Änderungen brauchen meist 5–60 Minuten).",
  "Resend → Webhooks: beim bestehenden Domivus-Webhook (…/api/webhooks/resend) zusätzlich das Ereignis „email.received“ aktivieren.",
  "Am Scanner/Kopierer unter „Scan to E-Mail“ die unten angezeigte Mandats-Adresse als Ziel (Adressbuch-Kurzwahl) hinterlegen.",
  "Optional: die Absenderadresse des Scanners unten als „erlaubter Absender“ eintragen — dann werden nur noch Scans von diesem Gerät angenommen.",
];

export default function ScanInboxSettings({ isAdmin }) {
  const [s, setS] = useState(null);
  const [senders, setSenders] = useState("");
  const load = () => api.get("/mailbox/scan-settings").then(({ data }) => { setS(data); setSenders((data.allowed_senders || []).join(", ")); });
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const copy = () => { navigator.clipboard?.writeText(s.address); toast.success("Adresse kopiert"); };
  const save = async (patch) => {
    try { await api.put("/mailbox/scan-settings", patch); toast.success("Scan-Einstellungen gespeichert"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const regen = async () => {
    if (!window.confirm("Neue Adresse erzeugen? Die alte Adresse nimmt danach keine Scans mehr an — der Scanner muss umgestellt werden.")) return;
    await api.post("/mailbox/scan-settings/regenerate"); toast.success("Neue Scan-Adresse erzeugt"); load();
  };
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-3" data-testid="scan-settings">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Printer size={16} /> Scan-Posteingang (Scanner → E-Mail)</div>
      <div className="flex flex-wrap items-center gap-2">
        <code className="px-2 py-1 rounded bg-slate-100 text-sm font-mono" data-testid="scan-address">{s.address}</code>
        <Button size="sm" variant="outline" className="gap-1" onClick={copy} data-testid="scan-address-copy"><Copy size={13} /> Kopieren</Button>
        {isAdmin && <Button size="sm" variant="outline" className="gap-1" onClick={regen} data-testid="scan-address-regenerate"><ArrowsClockwise size={13} /> Neu erzeugen</Button>}
        {isAdmin && (
          <label className="flex items-center gap-2 text-xs ml-auto">
            <Switch checked={s.enabled !== false} onCheckedChange={(v) => save({ enabled: v })} data-testid="scan-enabled-switch" /> Empfang aktiv
          </label>
        )}
      </div>
      {isAdmin && (
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label className="text-xs">Erlaubte Absender (leer = alle; z. B. scanner@ihre-firma.de oder @ihre-firma.de)</Label>
            <Input value={senders} onChange={(e) => setSenders(e.target.value)} placeholder="scanner@ihre-firma.de, @ihre-firma.de" data-testid="scan-senders-input" /></div>
          <Button size="sm" onClick={() => save({ allowed_senders: senders.split(/[,;\s]+/).filter(Boolean) })} data-testid="scan-senders-save">Speichern</Button>
        </div>
      )}
      {s.rejections?.length > 0 && (
        <div className="text-[11px] text-rose-700" data-testid="scan-rejections">
          Abgewiesen: {s.rejections.slice(0, 3).map((r) => `${r.from} (${fmtDateTime(r.created_at)})`).join(" · ")}
        </div>
      )}
      {isAdmin && <ScanInboundLog />}
      <details className="text-xs text-slate-600" data-testid="scan-setup-guide">
        <summary className="cursor-pointer font-medium text-slate-800">Einrichtung Schritt für Schritt</summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1">{STEPS(s.domain).map((t) => <li key={t}>{t}</li>)}</ol>
      </details>
    </div>
  );
}
